import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import { convertImageToPng } from "../util/image-to-png";

const MAX_LOGO_BYTES = 5 * 1024 * 1024;

export async function getOrganization(id) {
  const snap = await firebase.firestore().doc(`organizations/${id}`).get();
  if (!snap) return null;

  return snap.data();
}

export async function putOrganization(org, targetId) {
  const ref = targetId
    ? firebase.firestore().doc(`organizations/${targetId}`)
    : firebase.firestore().collection("organizations").doc();

  // Set the organization ID if it's a new organization
  if (!targetId) {
    org.id = ref.id;
  }

  await ref.set(org);

  return { message: "Organization successfully saved." };
}

export async function uploadOrganizationLogo(organizationId, file) {
  if (!organizationId) {
    throw new Error("An organization must be saved before adding a logo.");
  }

  const pngBlob = await convertImageToPng(file);

  if (pngBlob.size > MAX_LOGO_BYTES) {
    throw new Error("Logo image is too large. Please choose a smaller image.");
  }

  await firebase
    .storage()
    .ref(`${organizationId}/reportLogo.png`)
    .put(pngBlob, { contentType: "image/png" });

  return { message: "Logo successfully uploaded." };
}

export async function deleteOrganizationLogo(organizationId) {
  if (!organizationId) {
    throw new Error("An organization must be saved before removing a logo.");
  }

  await firebase
    .storage()
    .ref(`${organizationId}/reportLogo.png`)
    .delete();

  return { message: "Logo removed." };
}

export async function hasAvailableSeats(org) {
  const usersSnap = await firebase
    .firestore()
    .collection("users")
    .where("claims.organization", "==", org.id)
    .where("disabled", "==", false)
    .get();

  const orgSnap = await firebase
    .firestore()
    .doc(`organizations/${org.id}`)
    .get();

  const orgData = orgSnap.data();

  return usersSnap.size < orgData.subscription.seats;
}

export async function deactivateOrganization(organizationId) {
  const fn = firebase.functions().httpsCallable("disableOrganization");
  const result = await fn({ organizationId });
  return result.data.message;
}

export async function activateOrganization(organizationId) {
  const fn = firebase.functions().httpsCallable("enableOrganization");
  const result = await fn({ organizationId });
  return result.data.message;
}

/**
 * Sets, extends, or removes an organization's free trial. Super-admin only,
 * enforced by the callable — the trial decides who gets billed manually, so it
 * is deliberately not written straight to the organization document.
 * @param {string} organizationId
 * @param {string|null} trialEndsAt a yyyy-MM-dd date, or null to remove
 * @return {Promise<{message: string, trialEndsAt: string|null}>}
 */
export async function setOrganizationTrial(organizationId, trialEndsAt) {
  const fn = firebase.functions().httpsCallable("setOrganizationTrial");

  const result = await fn({
    organizationId: organizationId,
    trialEndsAt: trialEndsAt || null,
  });

  return result.data;
}

export async function updateSubscription(orgId, product, quantity) {
  const updateSubscriptionFn = firebase
    .functions()
    .httpsCallable("updateSubscription");

  const result = await updateSubscriptionFn({
    orgId: orgId,
    selectedProduct: {
      price: product,
      quantity: quantity,
    },
  });

  return result.data.message;
}
